/**
 * useAdminPayouts Hook
 *
 * Provides admin payout management with filtering and pagination
 * using real Firestore queries.
 */

import { useState, useEffect } from 'react';
import {
  collection,
  query,
  orderBy,
  getDocs,
  Timestamp,
} from 'firebase/firestore';
import { db } from '@/services/firebase';

export type PayoutStatus = 'requested' | 'processing' | 'completed' | 'rejected';

export interface AdminPayout {
  id: string;
  hostId: string;
  hostName: string;
  hostEmail: string;
  hostPhone?: string;
  hostPhoto?: string;
  amount: number;
  transferFee: number;
  netAmount: number;
  payoutMethodType: 'whish_money' | 'wire_transfer';
  payoutMethodDetails: {
    phoneNumber?: string;
    bankName?: string;
    iban?: string;
    swiftCode?: string;
    beneficiaryName?: string;
  };
  status: PayoutStatus;
  requestedAt: Date;
  processingStartedAt?: Date;
  completedAt?: Date;
  rejectedAt?: Date;
  transactionReference?: string;
  rejectionReason?: string;
  adminNote?: string;
  earningsCount: number;
}

export interface PayoutsFilter {
  status?: PayoutStatus | '';
  hostId?: string;
  search?: string;
  dateFrom?: string;
  dateTo?: string;
}

export interface PayoutsStats {
  total: number;
  requested: number;
  processing: number;
  completed: number;
  rejected: number;
  pendingAmount: number;
}

const PAGE_SIZE = 10;

const toDate = (val: any): Date => {
  if (val instanceof Timestamp) return val.toDate();
  if (val instanceof Date) return val;
  return val ? new Date(val) : new Date();
};

// Map 'failed' status from Firestore to 'rejected' for display
const mapPayoutStatus = (status: string): PayoutStatus => {
  if (status === 'failed') return 'rejected';
  if (['requested', 'processing', 'completed', 'rejected'].includes(status))
    return status as PayoutStatus;
  return 'requested';
};

export const useAdminPayouts = (filter: PayoutsFilter, page: number = 0) => {
  const [payouts, setPayouts] = useState<AdminPayout[]>([]);
  const [stats, setStats] = useState<PayoutsStats>({ total: 0, requested: 0, processing: 0, completed: 0, rejected: 0, pendingAmount: 0 });
  const [totalCount, setTotalCount] = useState(0);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    const fetchPayouts = async () => {
      setIsLoading(true);
      try {
        const [payoutsSnap, usersSnap] = await Promise.all([
          getDocs(query(collection(db, 'payouts'), orderBy('requestedAt', 'desc'))),
          getDocs(collection(db, 'users')),
        ]);

        // Build user lookup
        const userMap = new Map<string, { name: string; email: string; phone?: string; photo?: string }>();
        usersSnap.docs.forEach((d) => {
          const data = d.data();
          userMap.set(d.id, {
            name: data.displayName || `${data.firstName || ''} ${data.lastName || ''}`.trim(),
            email: data.email || '',
            phone: data.phone,
            photo: data.photoURL,
          });
        });

        let allPayouts: AdminPayout[] = payoutsSnap.docs.map((docSnap) => {
          const data = docSnap.data();
          const host = userMap.get(data.hostId) || { name: 'Unknown', email: '' };
          const methodDetails = data.payoutMethodDetails || {};
          const beneficiary = data.beneficiary || {};

          return {
            id: docSnap.id,
            hostId: data.hostId || '',
            hostName: host.name,
            hostEmail: host.email,
            hostPhone: host.phone,
            hostPhoto: host.photo,
            amount: data.amount || 0,
            transferFee: data.transferFee || 0,
            netAmount: data.netAmount || (data.amount || 0) - (data.transferFee || 0),
            payoutMethodType: data.payoutMethodType || 'whish_money',
            payoutMethodDetails: {
              phoneNumber: methodDetails.walletPhone || methodDetails.phoneNumber,
              bankName: methodDetails.bankName,
              iban: methodDetails.iban,
              swiftCode: methodDetails.swiftCode,
              beneficiaryName: beneficiary.name || methodDetails.beneficiaryName,
            },
            status: mapPayoutStatus(data.status),
            requestedAt: toDate(data.requestedAt),
            processingStartedAt: data.processedAt ? toDate(data.processedAt) : undefined,
            completedAt: data.completedAt ? toDate(data.completedAt) : undefined,
            rejectedAt: data.failedAt || data.rejectedAt ? toDate(data.failedAt || data.rejectedAt) : undefined,
            transactionReference: data.transactionReference,
            rejectionReason: data.failureReason || data.rejectionReason,
            adminNote: data.adminNote || data.adminNotes,
            earningsCount: data.earningIds?.length || 0,
          };
        });

        // Calculate stats from full dataset
        const payoutsStats: PayoutsStats = {
          total: allPayouts.length,
          requested: allPayouts.filter((p) => p.status === 'requested').length,
          processing: allPayouts.filter((p) => p.status === 'processing').length,
          completed: allPayouts.filter((p) => p.status === 'completed').length,
          rejected: allPayouts.filter((p) => p.status === 'rejected').length,
          pendingAmount: allPayouts
            .filter((p) => p.status === 'requested' || p.status === 'processing')
            .reduce((sum, p) => sum + p.amount, 0),
        };

        // Apply filters
        if (filter.status) {
          allPayouts = allPayouts.filter((p) => p.status === filter.status);
        }
        if (filter.hostId) {
          allPayouts = allPayouts.filter((p) => p.hostId === filter.hostId);
        }
        if (filter.search) {
          const searchLower = filter.search.toLowerCase();
          allPayouts = allPayouts.filter(
            (p) =>
              p.id.toLowerCase().includes(searchLower) ||
              p.hostName.toLowerCase().includes(searchLower) ||
              p.hostEmail.toLowerCase().includes(searchLower) ||
              p.transactionReference?.toLowerCase().includes(searchLower)
          );
        }

        // Sort: oldest first for pending, newest first for others
        allPayouts.sort((a, b) => {
          if (filter.status === 'requested') {
            return a.requestedAt.getTime() - b.requestedAt.getTime();
          }
          return b.requestedAt.getTime() - a.requestedAt.getTime();
        });

        if (!cancelled) {
          setStats(payoutsStats);
          setTotalCount(allPayouts.length);
          const start = page * PAGE_SIZE;
          setPayouts(allPayouts.slice(start, start + PAGE_SIZE));
          setIsLoading(false);
        }
      } catch (err) {
        console.error('Failed to fetch payouts:', err);
        if (!cancelled) {
          setPayouts([]);
          setTotalCount(0);
          setIsLoading(false);
        }
      }
    };

    fetchPayouts();
    return () => { cancelled = true; };
  }, [filter.status, filter.hostId, filter.search, filter.dateFrom, filter.dateTo, page]);

  return {
    payouts,
    stats,
    totalCount,
    isLoading,
    hasMore: (page + 1) * PAGE_SIZE < totalCount,
    totalPages: Math.ceil(totalCount / PAGE_SIZE),
  };
};

export default useAdminPayouts;
